import {
  Button,
  ButtonColor,
  ButtonWidth,
  IconCheck,
} from '@leland-dev/leland-ui-library';
import type { PortableTextTypeComponentProps } from '@portabletext/react';
import { type GraphQLFormattedError } from 'graphql';
import groq from 'groq';
import { type FC, type FormEvent, useState } from 'react';

import { type LelandImage } from '../../../types/cmsTypes';
import { DEFAULT_ERROR_MESSAGE } from '../../../utils/constants';
import SanityImage, { SANITY_IMAGE_PROJECTION } from '../../image/SanityImage';

import { useAssetDownloadSendSeoEmailCollectionMutation } from './__generated-gql-types__/AssetDownload.generated';

const ASSET_DOWNLOAD_TYPE = 'assetDownload';

export const ASSET_DOWNLOAD_MARK_DEFS_SELECTION = groq`_type == "${ASSET_DOWNLOAD_TYPE}" => {
  ...,
  image {${SANITY_IMAGE_PROJECTION}}
}`;

export interface AssetDownloadProps {
  _type: typeof ASSET_DOWNLOAD_TYPE;
  image: LelandImage;
  title: string;
  description: string;
  seoResource: string;
}

const AssetDownload: FC<
  PortableTextTypeComponentProps<AssetDownloadProps> & {
    categories?: Possible<string[]>;
  }
> = ({
  value: { image, title, description, seoResource } = {},
  categories,
}) => {
  const [email, setEmail] = useState<string>();
  const [subscribeModalError, setSubscribeModalError] =
    useState<readonly GraphQLFormattedError[]>();
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [sendSeoEmailCollectionAction, { loading: isSubmitting }] =
    useAssetDownloadSendSeoEmailCollectionMutation();
  const trimmedEmail = email?.trim();
  if (!image) {
    return null;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!trimmedEmail || !seoResource) return;
    const { errors } = await sendSeoEmailCollectionAction({
      variables: {
        email: trimmedEmail ?? '',
        resource: seoResource,
        categories,
      },
    });
    if (errors) {
      setSubscribeModalError(errors);
    } else {
      setSubscribeModalError([]);
      setIsSubmitSuccessful(true);
    }
  };

  return isSubmitSuccessful ? (
    <div className="relative my-20 flex w-full flex-col items-center justify-center rounded-2xl bg-leland-primary/10 px-8 py-12">
      <span className="rounded-full bg-leland-primary p-2">
        <IconCheck className="size-5 text-leland-white" />
      </span>
      <h3 className="mt-1 text-2xl font-medium text-leland-dark-green">
        Success!
      </h3>
      <p className="text-center text-lg text-leland-gray-light">
        Check your email for a link to download this resource.
      </p>
    </div>
  ) : (
    <div className="my-20 flex w-full flex-col rounded-2xl bg-leland-gray-hover px-8 md:flex-row">
      <SanityImage
        className="relative bottom-6 aspect-A4 w-32 rounded object-cover shadow-md"
        image={image}
        alt=""
        sizes={{ dflt: '200px' }}
      />
      <div className="flex grow flex-col pb-6 md:ml-10 md:py-6">
        <h3 className="text-2xl font-medium text-leland-gray-dark">{title}</h3>
        <p className="text-lg text-leland-gray-light">{description}</p>
        <form
          className="mt-4 flex w-full flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0"
          onSubmit={onSubmit}
        >
          <input
            className="w-full rounded-md border border-leland-gray-stroke p-3 text-leland-gray-dark"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            placeholder="Type your email..."
          />
          {(subscribeModalError?.length ?? 0) > 0 ? (
            <p className="rounded px-2 text-leland-red">
              {process.env.NEXT_PUBLIC_APPLICATION_ENV === 'production'
                ? DEFAULT_ERROR_MESSAGE
                : subscribeModalError?.map((val) => val.message).join('\n')}
            </p>
          ) : null}
          <Button
            type="submit"
            label="Submit"
            disabled={!trimmedEmail || isSubmitting}
            buttonColor={ButtonColor.PRIMARY}
            width={ButtonWidth.FULL_MOBILE}
            id="asset_download--submit_email_list_subscribe"
          />
        </form>
      </div>
    </div>
  );
};

export default AssetDownload;
